import { useQuery } from '@tanstack/react-query';

import { searchApi } from '@/services/search';
import {
  SearchOutcomesEnrichedResponse,
  SearchOutcomesParams,
} from '@/types/search';

export const SEARCH_OUTCOMES_ENRICHED_QUERY_KEY = 'search-outcomes-enriched';

export interface UseSearchOutcomesEnrichedOptions
  extends Omit<SearchOutcomesParams, 'queryText'> {
  queryText: string;
  enabled?: boolean;
}

const buildOutcomesParams = (
  options: UseSearchOutcomesEnrichedOptions
): SearchOutcomesParams => {
  const trimmedQuery = options.queryText.trim();

  return {
    queryText: trimmedQuery,
    ...(options.limit != null ? { limit: options.limit } : {}),
    ...(options.typeId ? { typeId: options.typeId } : {}),
    ...(options.categoryId ? { categoryId: options.categoryId } : {}),
    ...(options.resourceId ? { resourceId: options.resourceId } : {}),
    ...(options.contactId ? { contactId: options.contactId } : {}),
    ...(options.siteKey ? { siteKey: options.siteKey } : {}),
    ...(options.status ? { status: options.status } : {}),
    ...(options.result ? { result: options.result } : {}),
    ...(options.startDate ? { startDate: options.startDate } : {}),
    ...(options.endDate ? { endDate: options.endDate } : {}),
  };
};

export const useSearchOutcomesEnriched = ({
  queryText,
  limit = 10,
  typeId,
  categoryId,
  resourceId,
  contactId,
  siteKey,
  status,
  result,
  startDate,
  endDate,
  enabled = false,
}: UseSearchOutcomesEnrichedOptions) => {
  const params = buildOutcomesParams({
    queryText,
    limit,
    typeId,
    categoryId,
    resourceId,
    contactId,
    siteKey,
    status,
    result,
    startDate,
    endDate,
  });
  const isQueryReady = params.queryText.length >= 2;

  const query = useQuery<SearchOutcomesEnrichedResponse | undefined>({
    queryKey: [SEARCH_OUTCOMES_ENRICHED_QUERY_KEY, params],
    queryFn: ({ signal }) => searchApi.outcomesEnriched(params, signal),
    enabled: enabled && isQueryReady,
    staleTime: 5 * 60 * 1000,
  });

  return {
    data: query.data,
    relatedWorks: query.data?.relatedWorks,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isError: query.isError,
    error: query.error?.message ?? null,
    refetch: query.refetch,
  };
};
